import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import PageHeader from '@/components/ui/PageHeader';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import EmptyState from '@/components/ui/EmptyState';
import { motion } from 'framer-motion';
import { Heart, Plus, Edit, Trash2, User } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export default function AdminMinisterios() {
  const [showForm, setShowForm] = useState(false);
  const [editingMinistry, setEditingMinistry] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    leader_name: '',
    is_active: true
  });

  const queryClient = useQueryClient();

  const { data: ministries = [], isLoading } = useQuery({
    queryKey: ['adminMinistries'],
    queryFn: () => base44.entities.Ministry.list('name', 100)
  });

  const createMutation = useMutation({
    mutationFn: (data) => base44.entities.Ministry.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminMinistries'] });
      toast.success('Ministério criado com sucesso!');
      resetForm();
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.Ministry.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminMinistries'] });
      toast.success('Ministério atualizado!');
      resetForm();
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => base44.entities.Ministry.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminMinistries'] });
      toast.success('Ministério excluído!');
    }
  });

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      leader_name: '',
      is_active: true
    });
    setEditingMinistry(null);
    setShowForm(false);
  };

  const handleEdit = (ministry) => {
    setEditingMinistry(ministry);
    setFormData({
      name: ministry.name || '',
      description: ministry.description || '',
      leader_name: ministry.leader_name || '',
      is_active: ministry.is_active ?? true
    });
    setShowForm(true);
  };

  const handleSubmit = () => {
    if (!formData.name) {
      toast.error('Nome é obrigatório.');
      return;
    }

    if (editingMinistry) {
      updateMutation.mutate({ id: editingMinistry.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <div className="bg-gradient-to-br from-gray-600 to-gray-800 text-white pt-8 pb-6 px-4">
        <div className="max-w-lg mx-auto">
          <PageHeader 
            title="Ministérios" 
            subtitle={`${ministries.length} cadastrados`}
            showBack={true}
            action={
              <Button 
                size="sm" 
                variant="secondary"
                onClick={() => setShowForm(true)}
              >
                <Plus className="w-4 h-4 mr-1" />
                Novo
              </Button>
            }
          />
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6">
        {ministries.length === 0 ? (
          <EmptyState
            icon={Heart}
            title="Nenhum ministério cadastrado"
            description="Crie ministérios para organizar a igreja."
            actionLabel="Criar Ministério"
            onAction={() => setShowForm(true)}
          />
        ) : (
          <div className="space-y-4">
            {ministries.map((ministry, index) => (
              <motion.div
                key={ministry.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`bg-white rounded-xl shadow-sm p-4 ${!ministry.is_active ? 'opacity-60' : ''}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                    <Heart className="w-5 h-5 text-purple-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-gray-900 truncate">{ministry.name}</h3>
                      {!ministry.is_active && (
                        <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">Inativo</span>
                      )}
                    </div>
                    {ministry.description && (
                      <p className="text-sm text-gray-500 mt-1 line-clamp-2">{ministry.description}</p>
                    )}
                    {ministry.leader_name && (
                      <div className="flex items-center gap-1 mt-2 text-sm text-gray-500">
                        <User className="w-3 h-3" />
                        {ministry.leader_name}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex gap-2 mt-4 pt-3 border-t">
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1"
                    onClick={() => handleEdit(ministry)}
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Editar
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-red-600 border-red-200"
                    onClick={() => {
                      if (confirm('Excluir este ministério?')) {
                        deleteMutation.mutate(ministry.id);
                      }
                    }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingMinistry ? 'Editar Ministério' : 'Novo Ministério'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <Label>Nome do Ministério *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: Louvor e Adoração"
              />
            </div>

            <div>
              <Label>Descrição</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descrição do ministério"
                rows={2}
              />
            </div>

            <div>
              <Label>Líder</Label>
              <Input
                value={formData.leader_name}
                onChange={(e) => setFormData({ ...formData, leader_name: e.target.value })}
                placeholder="Nome do líder"
              />
            </div>

            <div className="flex items-center justify-between">
              <Label>Ministério ativo</Label>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button variant="outline" className="flex-1" onClick={resetForm}>
                Cancelar
              </Button>
              <Button 
                className="flex-1" 
                onClick={handleSubmit}
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                {editingMinistry ? 'Salvar' : 'Criar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}